import SimpleHeader from 'components/Headers/SimpleHeader.js';
import Pagination from 'components/Pagination';
import React, { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import Loader from 'react-loader-spinner';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Form,
  Row
} from 'reactstrap';
import { useGetQueues } from 'views/Report/hooks/useGetQueues';
import { definedMessageGeneratedInPdfCsvDownloads } from '../../utils/definedMessageGeneratePdfCsv';

import { buttonStyle } from 'components/Containers/ConfigurationContainer/components/utils';
import { FlexBootstrapTable } from 'components/FlexBoostrapTable';
import { FlexController } from 'components/FlexControllerInput';
import { ItemPerPageSelect } from 'components/Inputs/ItemPerPageSelect';
import ServiceCRUD from 'services/ServiceCRUDFLEXUC';
import { parseUnknownToSelectableOption } from 'utils/fuctions/parseUnknownToSelectableOption';
import { DatesHelper } from 'utils/classes/DatesHelper';
import ServiceCRUDChatTS from 'services/ServiceCRUDCHATTS';
import { useReportDownload } from './hooks/useReportDownload';
import { DownloadButtons } from './components/DownloadButtons';
import { ClearFiltersButton } from 'components/ClearFiltersButton';
/**
 * @typedef {'analytical' | 'synthetic'} TypeOfReport
 */

/**
 * @typedef {Object} Agent
 * @property {string} login - O login do usuário.
 * @property {string} name - O nome do usuário.
 * @property {string|null} pin_code - O código PIN do usuário.
 * @property {string} agent_code - O código do agent.
 * @property {boolean} active - Indica se o usuário está ativo.
 * @property {number|null} penalty - A penalidade do usuário.
 * @property {number} profileId - O ID do perfil do usuário.
 * @property {number} context - O contexto do usuário.
 * @property {boolean} isAutomaticLoginQueueChat - Indica se o login automático na fila de chat está ativado.
 * @property {number|null} costCenterId - O ID do centro de custo.
 * @property {boolean} webrtc_auto_login - Indica se o login automático no WebRTC está ativado.
 * @property {string|null} extension - A extensão do usuário.
 * @property {string|null} type_of_user - O tipo de usuário.
 * @property {string|null} type_of_pause - O tipo de pausa.
 * @property {boolean} dark_mode - Indica se o modo escuro está ativado.
 * @property {boolean} is_paused - Indica se o usuário está pausado.
 * @property {string|null} nickname - O apelido do usuário.
 * @property {boolean} isSupervisor - Indica se o usuário é supervisor.
 * @property {boolean} isAbleToEditSlaInfoMoreThanOnce - Indica se o usuário pode editar informações de SLA mais de uma vez.
 * @property {boolean} show_all_conversations - Indica se todas as conversas são exibidas.
 * @property {boolean} show_use_off_queues - Indica se o uso fora das filas é exibido.
 * @property {boolean} should_sign_in_asterisk - Indica se o usuário deve se registrar no Asterisk.
 * @property {number|null} scaleId - O ID da escala.
 * @property {boolean} show_all_protocols - Indica se todos os protocolos são exibidos.
 * @property {boolean} show_all_leads - Indica se todos os leads são exibidos.
 * @property {boolean} show_all_opportunities - Indica se todas as oportunidades são exibidas.
 * @property {number} teamId - O ID da equipe.
 * @property {string} email - O email do usuário.
 * @property {string} telephone - O telefone do usuário.
 * @property {string} integration_login - O login de integração do usuário.
 * @property {string} integration_password - A senha de integração do usuário.
 * @property {boolean} hasPermission - Indica se o usuário tem permissão.
 */

/**
 * @typedef {Object} LoginQueueReportAPIRequest
 * @property {string[]} queueIds - O nome da fila
 * @property {string[]} agentsLogin - O nome do agente
 * @property {string} dateIn - A data inicial do relatório no formato ISO
 * @property {string} dateOut - A data final do relatório no formato ISO
 * @property {string} duration_in_queue - A duração total do login
 * @property {'1' | '2'} report_type - 1 para relatório analítico, 2 para relatório sintético.
 *
 * @example
 *  {
    "queueIds": ["Comercial"],
    "agentsLogin": ["adm"],
    "dateIn": "2024-12-31T13:17:39.847Z",
    "dateOut": "2024-12-31T13:39:19.967Z",
    "duration_in_queue": "00:21:40",
    "report_type": "1"
  }
 */

const ReportService = new ServiceCRUDChatTS('/reports/login-queue-chat');
const UserService = new ServiceCRUD('/api/usuarios');

/**
 * @param formData - Um JSON com o conteúdo do formulário
 * @returns {LoginQueueReportAPIRequest}
 */
function parseFormDataToLoginQueueReportAPIRequest(formData) {
  return {
    duration_in_queue: formData.duration_in_queue,
    dateIn: formData.date_in
      ? DatesHelper.compileDateAndTime(formData.date_in, '00:00:00')
      : null,
    dateOut: formData.date_out
      ? DatesHelper.compileDateAndTime(formData.date_out, '23:59:59')
      : null,
    report_type:
      typeof formData.report_type?.value === 'undefined'
        ? '1'
        : String(formData.report_type.value) === '1'
        ? '1'
        : '2',
    queueIds: Array.isArray(formData.queue_id)
      ? formData.queue_id.map((queue) => queue.value)
      : formData.queue_id
      ? [formData.queue_id]
      : [],
    agentsLogin: Array.isArray(formData.agent_login)
      ? formData.agent_login.map((agent) => agent.value)
      : formData.agent_login
      ? [formData.agent_login]
      : []
  };
}

const QueueLoginReport = () => {
  const { control, handleSubmit, errors, setValue } = useForm();
  const systemConfiguration = useSelector((state) => state.systemConfiguration);
  const [itemsPerPage, setItemsPerPage] = useState(() => {
    const saved = localStorage.getItem('itemsPerPageAuthentication');
    return saved ? parseInt(saved) : 10;
  });

  /**
   * @typedef {Object} ReportState
   * @property {Array} analyticalReportData - Dados para relatório analítico.
   * @property {Array} syntheticalReportData - Dados para relatório sintético.
   * @property {TypeOfReport} selectedReportType - Tipo de relatório selecionado, seja 'analytical' ou 'synthetic'.
   * @property {Object} dataToSubmitToDownloads - Dados a serem enviados para downloads.
   * @property {number} itemsPerPage - Número de itens por página.
   * @property {boolean} showDisabledAgents - Flag para mostrar agentes desabilitados.
   * @property {boolean} showDisabledQueues - Flag para mostrar filas desabilitadas.
   * @property {Array} agents - Lista de agentes.
   * @property {boolean} loadingForm - Flag para indicar o estado de carregamento do formulário.
   * @property {boolean} loadingTable - Flag para indicar o estado de carregamento da tabela.
   * @property {number} activePage - Número da página ativa atual.
   * @property {number} totalItems - Número total de itens.
   * @property {string} messagemGenerateCsv - Mensagem para gerar CSV.
   * @property {string} messagemGenerateXlsx - Mensagem para gerar XLSX.
   */
  /** @type {[ReportState, React.Dispatch<React.SetStateAction<ReportState>>]} */
  const [reportState, setReportState] = useState({
    analyticalReportData: [],
    syntheticalReportData: [],
    selectedReportType: 'analytical',
    dataToSubmitToDownloads: null,
    itemsPerPage: 10,
    showDisabledAgents: false,
    showDisabledQueues: false,
    agents: [],
    loadingForm: false,
    loadingTable: false,
    activePage: 1,
    totalItems: 0,
    messagemGenerateCsv: '',
    messagemGenerateXlsx: ''
  });

  const limitGenerationPDF = parseInt(
    process.env.REACT_APP_LIMIT_GENERATION_PDF || 25000
  );

  const { queues } = useGetQueues(reportState.showDisabledQueues);
  const [queuesSelectableOptions] = useMemo(
    () => [
      queues
        ? queues.map((unk) => parseUnknownToSelectableOption(unk, 'name', 'id'))
        : []
    ],
    [queues]
  );
  const [agentSelectableOptions] = useMemo(
    () => [
      reportState.agents
        ? reportState.agents.map((unk) =>
            parseUnknownToSelectableOption(unk, 'name', 'login')
          )
        : []
    ],
    [reportState.agents]
  );

  useEffect(() => {
    const fetchAgents = async () => {
      setReportState((prevState) => ({ ...prevState, loadingForm: true }));
      try {
        const response = await UserService.getAll(
          false,
          undefined,
          reportState.showDisabledAgents
        );
        setReportState((prevState) => ({
          ...prevState,
          agents: response.data.data
        }));
      } catch (err) {
        console.log('err: ' + err);
      } finally {
        setReportState((prevState) => ({ ...prevState, loadingForm: false }));
      }
    };

    fetchAgents();
  }, [reportState.showDisabledAgents]);

  useEffect(() => {
    const message = definedMessageGeneratedInPdfCsvDownloads(
      reportState.totalItems,
      limitGenerationPDF
    );
    setReportState((prevState) => ({
      ...prevState,
      messagemGenerateCsv: message.csvMessage,
      messagemGenerateXlsx: message.xlsxMessage
    }));
  }, [reportState.totalItems, limitGenerationPDF]);

  const fetchReports = async (page, queryParams, reportType) => {
    const newActivePage = page || reportState.activePage;

    try {
      setReportState((prevState) => ({ ...prevState, loadingTable: true }));

      const response = await ReportService.getAllReportsType(
        reportType,
        newActivePage,
        { ...queryParams, page: newActivePage, size: itemsPerPage },
        itemsPerPage
      );

      if (reportType === 'analytical') {
        setReportState((prevState) => ({
          ...prevState,
          analyticalReportData: response.data.data.rows,
          totalItems: response.data.data.count
        }));
      } else {
        setReportState((prevState) => ({
          ...prevState,
          syntheticalReportData: [],
          totalItems: response.data.data.count
        }));
      }
    } catch (error) {
      setReportState((prevState) => ({
        ...prevState,
        analyticalReportData: [],
        syntheticalReportData: [],
        totalItems: 0
      }));
    } finally {
      setReportState((prevState) => ({
        ...prevState,
        loadingTable: false,
        activePage: newActivePage
      }));
    }
  };

  const onSubmit = async (formData) => {
    try {
      const parsedData = parseFormDataToLoginQueueReportAPIRequest(formData);

      const newSelectedReportType =
        parsedData.report_type === '1' ? 'analytical' : 'synthetic';

      setReportState((prevState) => ({
        ...prevState,
        selectedReportType: newSelectedReportType,
        dataToSubmitToDownloads: parsedData
      }));

      fetchReports(1, parsedData, newSelectedReportType);
    } catch (err) {
      console.log(err);
      toast.error(err || 'Erro desconhecido por favor, informar o suporte!', {
        autoClose: 3000,
        closeOnClick: true
      });
    }
  };

  useEffect(() => {
    localStorage.setItem('itemsPerPageAuthentication', itemsPerPage.toString());
  }, [itemsPerPage]);

  const handlePageChange = (activePage) => {
    fetchReports(
      activePage,
      reportState.dataToSubmitToDownloads,
      reportState.selectedReportType
    );
  };

  const handleReset = () => {
    setValue('date_in', DatesHelper.getTodayInBrazilianFormat());
    setValue('date_out', DatesHelper.getTodayInBrazilianFormat());
    setValue('queue_id', []);
    setValue('agent_login', []);

    setReportState((prev) => ({
      ...prev,
      analyticalReportData: [],
      syntheticalReportData: [],
      selectedReportType: 'analytical',
      dataToSubmitToDownloads: null,
      activePage: 1,
      totalItems: 0
    }));
  };

  const { downloadType, downloading, handleDownload } =
    useReportDownload(ReportService);

  const downloadButtons = (
    <DownloadButtons
      disabled={
        !reportState.dataToSubmitToDownloads ||
        reportState.totalItems > limitGenerationPDF
      }
      onCsvClick={() =>
        handleDownload('csv', reportState.dataToSubmitToDownloads)
      }
      onXlsxClick={() =>
        handleDownload('xlsx', reportState.dataToSubmitToDownloads)
      }
      downloading={downloading}
      downloadType={downloadType}
      csvTooltip={reportState.messagemGenerateCsv}
      xlsxTooltip={reportState.messagemGenerateXlsx}
    />
  );

  return (
    <>
      <SimpleHeader
        name="Form validation"
        parentName="Forms"
        showBackArrow="not"
      />
      <Container className="mt--6" fluid>
        <Row>
          <div className="col">
            <div className="card-wrapper">
              <Card>
                <CardHeader>
                  <h3>Relatório de autenticação em filas do chat</h3>
                </CardHeader>
                <CardBody>
                  {reportState.loadingForm ? (
                    <Row className="justify-content-md-center">
                      <Loader
                        type="TailSpin"
                        color={systemConfiguration.primary_color}
                        height={100}
                        width={100}
                      />
                    </Row>
                  ) : (
                    <>
                      <Form
                        className="needs-validation"
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          rowGap: '1rem'
                        }}
                        onSubmit={handleSubmit(onSubmit)}
                      >
                        <ClearFiltersButton
                          styles={{
                            position: 'absolute',
                            right: '1rem',
                            top: '2rem'
                          }}
                          handleOnClick={handleReset}
                        />
                        <Row>
                          <Col md="6">
                            <FlexController
                              type="selectable"
                              textoDoLabel="Fila(s)"
                              name="queue_id"
                              control={control}
                              options={queuesSelectableOptions}
                              isMulti={true}
                              isClearable={true}
                              closeMenuOnSelect={false}
                              valueController={setValue}
                              errors={errors}
                            />
                          </Col>
                          <Col md="6">
                            <FlexController
                              type="selectable"
                              textoDoLabel="Agente"
                              name="agent_login"
                              control={control}
                              options={agentSelectableOptions}
                              isMulti={true}
                              isClearable={true}
                              closeMenuOnSelect={false}
                              valueController={setValue}
                              errors={errors}
                            />
                          </Col>
                        </Row>

                        <Row>
                          <Col md="6">
                            <FlexController
                              textoDoLabel="Data de início"
                              type="date"
                              name="date_in"
                              valueController={setValue}
                              errors={errors}
                              control={control}
                              rules={{
                                required: 'Data de início é obrigatória!',
                                pattern: {
                                  value:
                                    /^[0-3][0-9]\/[0-1][0-9]\/[0-9][0-9][0-9][0-9]$/,
                                  message: 'Data inválida! (dd/MM/aaaa)'
                                }
                              }}
                            />
                          </Col>
                          <Col md="6">
                            <FlexController
                              textoDoLabel="Data de término"
                              type="date"
                              name="date_out"
                              valueController={setValue}
                              errors={errors}
                              control={control}
                              rules={{
                                required: 'Data de término é obrigatória!',
                                pattern: {
                                  value:
                                    /^[0-3][0-9]\/[0-1][0-9]\/[0-9][0-9][0-9][0-9]$/,
                                  message: 'Data inválida! (dd/MM/aaaa)'
                                }
                              }}
                            />
                          </Col>
                        </Row>
                        <Row>
                          <Col
                            style={{
                              display: 'flex',
                              columnGap: '1rem',
                              alignItems: 'center'
                            }}
                          >
                            <FlexController
                              textoDoLabel="Mostrar desabilitadas (filas)"
                              type="checkbox"
                              name="show_disabled_queues"
                              checked={reportState.showDisabledQueues}
                              onChange={(event) =>
                                setReportState({
                                  ...reportState,
                                  showDisabledQueues:
                                    event.target.checked ?? false
                                })
                              }
                              control={control}
                            />
                            <FlexController
                              textoDoLabel="Mostrar desabilitados (agentes)"
                              type="checkbox"
                              name="show_disabled_agents"
                              checked={reportState.showDisabledAgents}
                              onChange={(event) =>
                                setReportState({
                                  ...reportState,
                                  showDisabledAgents:
                                    event.target.checked ?? false
                                })
                              }
                              control={control}
                            />
                          </Col>
                        </Row>
                        <Row>
                          <Col
                            style={{
                              display: 'flex',
                              columnGap: '.75rem',
                              alignItems: 'center'
                            }}
                          >
                            <Button
                              style={buttonStyle(
                                systemConfiguration.primary_color
                              )}
                              type="submit"
                            >
                              Buscar
                            </Button>
                            <ItemPerPageSelect
                              valueController={setItemsPerPage}
                            />
                          </Col>
                        </Row>
                      </Form>
                      {downloadButtons}
                    </>
                  )}
                </CardBody>
                {reportState.loadingTable ? (
                  <div className="py-4 table-responsive">
                    <Row className="justify-content-md-center">
                      {reportState.loadingTable && (
                        <Loader
                          type="TailSpin"
                          color="#35ac50"
                          height={100}
                          width={100}
                        />
                      )}
                    </Row>
                  </div>
                ) : (
                  <>
                    <FlexBootstrapTable
                      data={reportState.analyticalReportData || []}
                      totalRecords={reportState.totalItems}
                      columns={[
                        {
                          dataField: 'agent_name',
                          text: 'Nome do agente',
                          sort: true
                        },
                        {
                          dataField: 'queue_name',
                          text: 'Nome da fila',
                          sort: true
                        },
                        {
                          dataField: 'date_in',
                          text: 'Data de entrada',
                          sort: true,
                          formatter: (val) =>
                            val
                              ? DatesHelper.convertISOToBrazilianDateTimeFormat(
                                  val
                                )
                              : ''
                        },
                        {
                          dataField: 'date_out',
                          text: 'Data de saída',
                          sort: true,
                          formatter: (val) =>
                            val
                              ? DatesHelper.convertISOToBrazilianDateTimeFormat(
                                  val
                                )
                              : ''
                        },
                        {
                          dataField: 'duration_in_queue',
                          text: 'Duração do login',
                          sort: true
                        }
                      ]}
                    />

                    {reportState.dataToSubmitToDownloads && (
                      <Pagination
                        itemsCountPerPage={itemsPerPage}
                        activePage={reportState.activePage}
                        totalItemsCount={reportState.totalItems}
                        onChange={handlePageChange.bind(this)}
                      />
                    )}
                  </>
                )}
              </Card>
            </div>
          </div>
        </Row>
      </Container>
    </>
  );
};

export default QueueLoginReport;
